import { Word } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as admin from '../../admin'

import { Purpose } from './purpose'

/**
 * Represents an
 * {@link https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html S3 object key}
 */
export type Key = string
export namespace Key {
  const DIR_SEPARATOR = '/'
  const FILE_EXTENSION_SEPARATOR = '.'

  /**
   * Splits a filename into its base name and extension.
   *
   * @example
   * extractExtension("file.xlsx") // { name: "file", extension: "xlsx" }
   * extractExtension("file.backup.xlsx") // { name: "file.backup", extension: "xlsx" }
   * extractExtension("file") // { name: "file", extension: undefined }
   */
  function extractExtension(filename: string): {
    name: string
    extension: string | undefined
  } {
    const lastDotIndex = filename.lastIndexOf(FILE_EXTENSION_SEPARATOR)
    return lastDotIndex !== -1
      ? {
          extension: filename.substring(lastDotIndex + 1),
          name: filename.substring(0, lastDotIndex),
        }
      : { extension: undefined, name: filename }
  }

  /**
   * Generates a {@link Key} from the given {@link Key.Parts.Input}
   */
  export function from(input: Parts.Input): Key {
    const { context, extension, id, purpose, qualifier } = Parts.from(input)
    const filename = `${id}${extension ? `${FILE_EXTENSION_SEPARATOR}${extension}` : ''}`
    // Extend context with qualifier for folder generation
    const extendedContext = { ...context, qualifier }
    /** NOTE: must match {@link parse} */
    const segments = [
      ...Key.Parts.Context.asFolders(extendedContext),
      purpose,
      filename,
    ]
    return segments.join(DIR_SEPARATOR)
  }

  /**
   * Extracts {@link Key.Parts} from a given {@link Key}
   */
  export function parse(key: Key): Parts {
    /** NOTE: must match {@link from} */
    const path = key.split(DIR_SEPARATOR)
    // File name is the last part of the path
    const fileName = path.pop()
    TypeAsserter.assertIsNotNullish(fileName)
    // Purpose folder is second-to-last part of the path
    const purpose = path.pop()
    Purpose.assertIsPurpose(purpose)
    // Context (including qualifier) can be extracted from the remaining folders in the path
    const { qualifier, ...context } = Parts.Context.fromFolders(path)
    // ID and extension can be extracted from the file name
    const { name: id, extension } = extractExtension(fileName)
    return {
      context,
      extension,
      id,
      purpose,
      qualifier,
    }
  }

  /**
   * Returns canonical {@link Key.Parts} for an object key, or `undefined` when
   * the key is a folder marker, malformed, or cannot be reconstructed exactly.
   */
  export function tryParse(key: Key): Parts | undefined {
    if (key.endsWith(DIR_SEPARATOR)) {
      return undefined
    }

    try {
      const parts = parse(key)
      return from(parts) === key ? parts : undefined
    } catch {
      return undefined
    }
  }

  /**
   * Represents all of the information encoded in a {@link Key}
   */
  // NOTE: this references Parts.Input, rather than the other way around,
  // because that makes it more reasonable for us to put @default tags in the
  // JSDoc comments below
  export type Parts = Required.Semi<Parts.Input, 'id' | 'purpose'>
  export namespace Parts {
    export const isParts = TypeChecker.Factory.object<Parts>({
      context: admin.Context.ForAny.isForAny,
      extension: TypeChecker.Factory.optional(TypeChecker.isString),
      id: TypeChecker.isString,
      purpose: Purpose.isPurpose,
      qualifier: TypeChecker.isString,
    })

    /**
     * Returns whether a file belongs to the current user's zone: either as a
     * private object owned by that user or as a shared zone object.
     */
    export function isInUserZone(
      file: Pick<Parts, 'context'>,
      context: admin.Context.ForUser.InZone,
    ): boolean {
      const fileContext = file.context
      const fileUserId = admin.Context.ForUser.isForUser(fileContext)
        ? fileContext.userId
        : undefined
      return (
        admin.Context.ForZone.isForZone(fileContext) &&
        fileContext.tenantId === context.tenantId &&
        fileContext.zoneId === context.zoneId &&
        (fileUserId === undefined || fileUserId === context.userId)
      )
    }

    /**
     * Returns the full {@link Parts} from {@link Parts.Input}
     *
     * I.e., this applies defaults to any property that has a default
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from({
      context,
      id: inputId = Word.generate(),
      extension: inputExtension,
      purpose = Purpose.FileManagement,
      qualifier = undefined,
    }: Input): Parts {
      // If no extension provided, try to extract it from the id (e.g., "file.xlsx" → id: "file", extension: "xlsx")
      const { name: id, extension } =
        inputExtension === undefined
          ? extractExtension(inputId)
          : { extension: inputExtension, name: inputId }

      return {
        context,
        extension,
        id,
        purpose,
        qualifier,
      } satisfies NoMissing<Parts>
    }

    /**
     * {@link Key.Parts} with certain properties that have defaults as optional
     */
    export interface Input {
      /**
       * Determines what this {@link Key} is associated with
       *
       * @see {@link Context}
       */
      readonly context: Context
      /**
       * Indicates what the object is intended for
       *
       * @default Purpose.FileManagement
       */
      readonly purpose?: Purpose
      /**
       * Uniquely identifies the {@link Key} within the given {@link context} and
       * {@link purpose}
       *
       * @default generated
       */
      readonly id?: string
      /**
       * Optional qualifier appended to the {@link id} to distinguish files
       * with the same {@link id} and {@link extension}.
       *
       * Useful for associating files with specific contexts (e.g., message ID,
       * version number, timestamp) while maintaining the same base identifier.
       *
       * @default undefined
       * @example
       * // file.pdf → id: "file", qualifier: '1633024876000'
       * // file.pdf → id: "file", qualifier: "msg-123"
       * // Results in keys that can differentiate identical filenames
       */
      readonly qualifier?: string
      /**
       * Usually the file extension (without the dot)
       */
      readonly extension?: string
    }
    export namespace Input {
      export const isInput = TypeChecker.Factory.object<Input>({
        context: admin.Context.ForAny.isForAny,
        extension: TypeChecker.Factory.optional(TypeChecker.isString),
        id: TypeChecker.Factory.optional(TypeChecker.isString),
        purpose: TypeChecker.Factory.optional(Purpose.isPurpose),
        qualifier: TypeChecker.Factory.optional(TypeChecker.isString),
      })
    }

    /**
     * S3 objects can be associated with anything represented by
     * {@link admin.Context.ForAny}
     */
    export type Context = admin.Context.ForAny
    export namespace Context {
      /**
       * Extended context that includes qualifier for folder generation
       */
      export type WithQualifier = Context & { qualifier?: string }

      /**
       * Represents {@link Context} (optionally with qualifier) as a series of folders (strings)
       */
      export function asFolders(context: WithQualifier): string[] {
        const folders: string[] = []
        for (const [parentFolder, key] of ParentFolder.PROPERTY_MAP) {
          const id = (context as FlattenUnion<typeof context>)[key]
          if (id) {
            // If the context has this ID, we can add the parent folder and then the ID in that parent folder
            folders.push(parentFolder, id)
          }
        }
        return folders
      }

      /** Returns the S3 key prefix for objects within the given context. */
      export function asPrefix(context: WithQualifier): string {
        return `${asFolders(context).join(DIR_SEPARATOR)}${DIR_SEPARATOR}`
      }

      /** Returns the equivalent context for a shared object key. */
      export function withoutUser(context: Context): Context {
        if (!admin.Context.ForUser.isForUser(context)) {
          return context
        }

        const { userId: _, ...sharedContext } = context
        return admin.Context.ForAny.fromIds(sharedContext)
      }
      /**
       * Extracts a {@link Context} (with optional qualifier) from a series of folders (strings)
       */
      export function fromFolders(folders: readonly string[]): WithQualifier {
        const context: Partial<FlattenUnion<WithQualifier>> = {}
        // Iterate through every folder
        for (let i = 1; i < folders.length; i++) {
          const parentFolder = folders[i - 1]
          if (!ParentFolder.isParentFolder(parentFolder)) {
            // If the previous folder is not a parent folder, then this folder
            // is not a context ID
            continue
          }
          const id = folders[i]
          const contextKey = ParentFolder.PROPERTY_MAP.get(parentFolder)
          if (contextKey) {
            // If we were able to determine the context key for the parent
            // folder, then we can add this ID to the context
            context[contextKey] = id
          }
          // We never have two parent folders in a row, so since we just
          // encountered a parent folder, we can skip forward one
          i++
        }
        return context as Context
      }

      /**
       * Used as part of the {@link Key} to represent a folder containing items
       * of the given type
       */
      export enum ParentFolder {
        User = 'U',
        Tenant = 'T',
        Zone = 'Z',
        App = 'A',
        Scenario = 'S',
        /** Used for distinguishing files with the same name */
        Qualifier = 'Q',
      }
      // Needs to be before namespace is defined
      const PARENT_FOLDERS: ReadonlySet<ParentFolder> = new Set(
        Object.values(ParentFolder) as ParentFolder[],
      )
      export namespace ParentFolder {
        export const ALL: readonly ParentFolder[] = Array.from(PARENT_FOLDERS)
        export const isParentFolder = TypeChecker.Factory.oneOf(PARENT_FOLDERS)
        export const assertIsParentFolder: TypeAsserter<ParentFolder> =
          TypeAsserter.Factory.from(isParentFolder)

        /**
         * __NOTE:__ this is a {@link Map} since the entries are sorted based
         * on the order they should appear in
         */
        export const PROPERTY_MAP = new Map<
          ParentFolder,
          globalThis.Key.In.Union<WithQualifier>
        >([
          [ParentFolder.User, 'userId'],
          [ParentFolder.Tenant, 'tenantId'],
          [ParentFolder.Zone, 'zoneId'],
          [ParentFolder.App, 'appId'],
          [ParentFolder.Scenario, 'scenarioId'],
          [ParentFolder.Qualifier, 'qualifier'],
        ])
      }
    }
  }
}
